import React, { Component } from 'react';
import PropTypes from 'prop-types';
import MdArrowDropDown from 'react-icons/lib/md/arrow-drop-down';
import MdArrowDropUp from 'react-icons/lib/md/arrow-drop-up';
import MdClear from 'react-icons/lib/md/clear';
import DatePicker from 'react-datepicker';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import moment from 'moment';
// eslint-disable-next-line
import { getCompanyDashboardPath } from '~services/utils';
import 'isomorphic-fetch';

import messages from './messages/LicenseTable';
import propMessages from './messages/LicenseProps';
import './styles/LicenseTable.scss';

import Button from '../../components/Button';
import { Input, InputGroup, Select } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';

const Sort = ({ asc, desc }) =>
  <div styleName='col-sort'>
    {asc && <MdArrowDropUp />}
    {desc && <MdArrowDropDown />}
  </div>;
Sort.propTypes = {
  asc: PropTypes.bool,
  desc: PropTypes.bool,
}
Sort.defaultProps = {
  asc: false,
  desc: false,
};

const Cell = ({ children, onClick, sortCol }) =>
  <td
    data-sort={sortCol}
    onClick={onClick}
    styleName='cell'
  >
    <div styleName='cell-content'>
      {children}
    </div>
  </td>;
Cell.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  sortCol: PropTypes.bool,
};
Cell.defaultProps = {
  children: [],
  onClick: () => {},
  sortCol: false,
};

const Row = ({ active, children, clickHandler, header }) =>
  <tr
    styleName='row'
    data-active={active}
    data-header={header}
    onClick={clickHandler}
  >
    {children}
  </tr>;
Row.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  clickHandler: PropTypes.func,
  header: PropTypes.bool,
};
Row.defaultProps = {
  active: false,
  header: false,
  children: [],
  clickHandler: () => {},
};

const propTypes = {
  cols: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  limitOptions: PropTypes.array,
  licenseTypes: PropTypes.array.isRequired,
  onChangeTableView: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onFetch: PropTypes.func.isRequired,
  pagination: PropTypes.object.isRequired,
  rows: PropTypes.array.isRequired,
  schema: PropTypes.object.isRequired,
  view: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  retailers: PropTypes.array.isRequired,
  retailerPersons: PropTypes.array.isRequired,
};

const defaultProps = {
  limitOptions: [5, 25, 125, 500],
};

class LicenseTable extends Component {
  constructor(props) {
    super(props);

    this.LICENSE_VIEW = 'licenses';
    this.BILLING_VIEW = 'billing';
    this.ORDER_ASC = 1;
    this.ORDER_DESC = -1;
    this.FILTER_OPTION_ALL = 'All';
    this.STATUS_OPTIONS = [
      'ordered',
      'pending',
      'terminated',
    ];

    this.searchDebouncer = null;

    const {
      filters: { dateFrom, dateTo, dateToFilter, searchString },
      pagination: {
        exportCacheId,
        limit,
        offset,
        order,
        orderBy,
        searchCacheId,
        total,
      },
    } = props;
    this.state = {
      activeRows: [],
      dateFrom: dateFrom ? moment(dateFrom) : null,
      dateTo: dateTo ? moment(dateTo) : null,
      dateToFilter,
      exportCacheId,
      limit,
      offset,
      order: order.toLowerCase() === 'asc' ? this.ORDER_ASC : this.ORDER_DESC,
      orderBy,
      searchCacheId,
      searchString,
      statusToFilter: this.FILTER_OPTION_ALL,
      licenseTypeToFilter: this.FILTER_OPTION_ALL,
      retailerToFilter: this.FILTER_OPTION_ALL,
      retailerPersonToFilter: this.FILTER_OPTION_ALL,
      total,
      qvarnResourceCacheId: Date.now(),
    };
  }

  componentDidUpdate = prevProps => {
    let newState = {}
    if (prevProps.view !== this.props.view) {
      const {
        filters: { dateFrom, dateTo, dateToFilter, searchString },
        pagination: { limit, offset, order, orderBy, total },
      } = this.props;
      newState = {
        ...newState,
        dateFrom,
        dateTo,
        dateToFilter,
        limit,
        offset,
        order: order.toLowerCase() === 'asc' ? this.ORDER_ASC : this.ORDER_DESC,
        orderBy,
        searchString,
        total,
      };
    }
    const { pagination: { exportCacheId, searchCacheId } } = this.props;
    if (prevProps.pagination.searchCacheId !== searchCacheId
        || prevProps.pagination.exportCacheId !== exportCacheId) {
      newState = { ...newState, exportCacheId, searchCacheId };
    }
    if (Object.keys(newState).length) {
      this.setState(newState);
    }
  }

  getRowClickHandler = rowId => evnt => {
    const target = evnt.target;
    if (target.getAttribute('data-click-handler') !== 'ignore') {
      evnt.preventDefault();
      const { activeRows } = this.state;
      const ctrl = evnt.ctrlKey
      const newActiveRows = activeRows.filter(rid => rid !== rowId && ctrl);
      if (activeRows.indexOf(rowId) === -1) {
        newActiveRows.push(rowId);
      }
      this.setState({ activeRows: newActiveRows });
    }
  }

  getViewSwitcher = view => evnt => {
    evnt.preventDefault();
    const { onChangeTableView } = this.props;
    onChangeTableView(view);
  }

  getLimitSwitcher = limit => evnt => {
    evnt.preventDefault();
    this.paginatorChanger({ limit, offset: 0 });
  }

  getOffsetSwitcher = offset => evnt => {
    evnt.preventDefault();
    this.paginatorChanger({ offset });
  }

  getSortSwitcher = col => evnt => {
    evnt.preventDefault();
    const { order, orderBy } = this.state;
    const { pagination: { limit, total } } = this.props;
    const shouldUpdate = limit !== total;
    let nextOrder = this.ORDER_DESC;
    if (orderBy === col) {
      nextOrder = order === this.ORDER_ASC ? this.ORDER_DESC : this.ORDER_ASC;
    }
    this.paginatorChanger(
      { order: nextOrder, orderBy: col, offset: 0 },
      shouldUpdate,
    );
  }

  getDateRangeSelector = dateType => value => {
    this.filterChanger({ [dateType]: value });
  }

  selectDateToFilter = evnt => {
    const dateToFilter = evnt.target.value;
    this.filterChanger({ dateToFilter });
  }

  selectStatusToFilter = evnt => {
    const statusToFilter = evnt.target.value;
    this.filterChanger({ statusToFilter, searchCacheId: null });
  }

  selectLicenseTypeToFilter = evnt => {
    const licenseTypeToFilter = evnt.target.value;
    this.filterChanger({ licenseTypeToFilter, searchCacheId: null });
  }

  selectRetailerToFilter = evnt => {
    const retailerToFilter = evnt.target.value;
    this.filterChanger({ retailerToFilter, searchCacheId: null });
  }

  selectRetailerPersonToFilter = evnt => {
    const retailerPersonToFilter = evnt.target.value;
    this.filterChanger({ retailerPersonToFilter, searchCacheId: null });
  }

  search = evnt => {
    const searchString = evnt.target.value;
    this.filterChanger({ searchString });
  }

  filterChanger = changes => {
    const actuallyChanged = Object.keys(changes).filter(
      change => changes[change] !== this.state[change]
    );
    if (actuallyChanged.length) {
      this.setState({ ...changes, offset: 0 });
    }
  }

  paginatorChanger = (changes, shouldUpdate=true) => {
    const actuallyChanged = Object.keys(changes).filter(
      change => changes[change] !== this.state[change]
    );
    if (actuallyChanged.length) {
      this.setState(changes, () => shouldUpdate && this.updateRows());
    }
  }

  updateRows = () => {
    const { onFetch } = this.props;
    this.setState({ exportCacheId: null }, () => onFetch({
      andFilters: this.andFilters,
      orFilters: this.orFilters,
      pagination: this.pagination,
      searchTerms: this.searchTerms,
    }));
  }

  get dateCols() {
    const { cols, schema } = this.props;
    const dateCols = cols.reduce((dc, c) => {
      if (schema[c] && schema[c].type === 'date') {
        dc.push(c);
      }
      return dc;
    }, []);
    return dateCols;
  }

  get textCols() {
    const { cols, schema } = this.props;
    const textCols = cols.reduce((tc, c) => {

      if (schema[c] && schema[c].type === 'text') {
        tc.push(c);
      }
      return tc;
    }, []);
    return textCols;
  }

  get andFilters() {
    const {
      dateFrom,
      dateTo,
      dateToFilter,
      licenseTypeToFilter,
      retailerToFilter,
      retailerPersonToFilter,
      statusToFilter,
    } = this.state;
    const andFilters = [];

    if (dateToFilter) {
      let field = dateToFilter;
      if (dateToFilter.indexOf('.') !== -1) {
        field = dateToFilter.split('.')[1];
      }
      if (dateFrom) {
        andFilters.push(`${field}>=${dateFrom.format('YYYY-MM-DD')}`);
      }
      if (dateTo) {
        andFilters.push(`${field}<=${dateTo.format('YYYY-MM-DD')}`);
      }
    }
    if (licenseTypeToFilter !== this.FILTER_OPTION_ALL) {
      andFilters.push(`licenseTypeId=${licenseTypeToFilter}`);
    }
    if (retailerToFilter !== this.FILTER_OPTION_ALL) {
      andFilters.push(`retailerId=${retailerToFilter}`);
    }
    if (retailerPersonToFilter !== this.FILTER_OPTION_ALL) {
      andFilters.push(`retailerPersonId=${retailerPersonToFilter}`);
    }
    if (statusToFilter !== this.FILTER_OPTION_ALL) {
      andFilters.push(`status=${statusToFilter}`);
    }
    return andFilters;
  }

  get orFilters() {
    const { searchString } = this.state;
    let orFilters = [];
    const ss = searchString.trim();
    if (ss) {
      orFilters = this.textCols.map(tc =>
        `${tc.split('.').join('_')}ILIKE${ss}`
      );
    }
    return orFilters;
  }

  get pagination() {
    const {
      exportCacheId,
      limit,
      offset,
      total,
      order,
      orderBy,
      searchCacheId,
      qvarnResourceCacheId,
    } = this.state;
    return {
      exportCacheId,
      limit,
      offset,
      total,
      order: order === this.ORDER_ASC ? 'ASC' : 'DESC',
      orderBy,
      searchCacheId,
      qvarnResourceCacheId,
    };
  }

  get orderedRows() {
    const { rows } = this.props;
    const { order, orderBy} = this.state;
    return rows.sort((a, b) => {
      const orderByA = a.toComparable(orderBy);
      const orderByB = b.toComparable(orderBy);
      if (orderByA > orderByB) return this.ORDER_ASC * order;
      else if (orderByA < orderByB) return this.ORDER_DESC * order;
      return 0;
    });
  }

  showUnpaid = evnt => {
    evnt.preventDefault();
    this.filterChanger({
      dateToFilter: 'paidAt',
      dateFrom: null,
      dateTo: null,
      dateNotSet: true,
    });
  }

  showUnbilled = () => {
    this.filterChanger({
      dateToFilter: 'billedAt',
      dateFrom: null,
      dateTo: null,
      dateNotSet: true,
    });
  }

  exportExcel = evnt => {
    evnt.preventDefault();
    const { onExport } = this.props;
    onExport({
      andFilters: this.andFilters,
      orFilters: this.orFilters,
      pagination: this.pagination,
    });
  }

  renderPagination = () => {
    const { pagination: { limit, offset, total }} = this.props;
    const pages = Math.ceil(total / (limit || total));
    const currentPage = offset / (limit || total);
    const options = Array.from({ length: pages }, (_, page) => {
      const pageNo = page + 1;
      const o = page * limit;
      return (
        <button
          styleName={page === currentPage ? 'active' : ''}
          key={`licenses-pagination-page-${page}`}
          onClick={this.getOffsetSwitcher(o)}
        >
          {pageNo}
        </button>
      );
    });
    return (
      <div styleName='view-controls'>
        <strong>Page:</strong>
        {options}
      </div>
    );
  }

  renderLimiter = () => {
    const { pagination: { limit, total }} = this.props;
    const options = this.props.limitOptions.filter(l => l < total).map(l =>
        <button
          styleName={limit === l ? 'active' : ''}
          key={`licenses-pagination-limit-${l}`}
          onClick={this.getLimitSwitcher(l)}
        >
          {l}
        </button>
    );
    // Add "All"-limit, which is 'active' if total results <= limit
    options.push(
      <button
        styleName={limit >= total ? 'active' : ''}
        key='licenses-pagination-limit-all'
        onClick={this.getLimitSwitcher(total)}
      >
        All({total})
      </button>
    );
    return (
      <React.Fragment>
        <strong>Results per page:</strong>
        {options}
      </React.Fragment>
    )
  }

  render = () => {
    const { intl } = this.props;
    const {
      activeRows,
      dateToFilter,
      dateTo,
      licenseTypeToFilter,
      order,
      orderBy,
      retailerToFilter,
      retailerPersonToFilter,
      searchString,
      statusToFilter,
      dateFrom } = this.state;
    const { cols, loading } = this.props;
    return (
      <div styleName='LicenseTable'>
        <div styleName='view-controls'>
          {/* Only license view enabled for now.
          <strong>
            <FormattedMessage {...messages.licensetableViewTitle} />
          </strong>
          <button
            styleName={this.props.view === this.LICENSE_VIEW ? 'active' : ''}
            onClick={this.getViewSwitcher(this.LICENSE_VIEW)}
          >
            <FormattedMessage {...messages.licensetableLicensesViewLabel} />
          </button>
          <button
            styleName={this.props.view === this.BILLING_VIEW ? 'active' : ''}
            onClick={this.getViewSwitcher(this.BILLING_VIEW)}
          >
            <FormattedMessage {...messages.licensetableBillingViewLabel} />
          </button>
          <span> | </span>
          */}
          {this.renderLimiter()}
        </div>
        <div styleName='table-controls'>
          <InputGroup>
            <span>License status</span>
            <Select onChange={this.selectStatusToFilter} value={statusToFilter}>
              <option value={this.FILTER_OPTION_ALL} key="statuscol-all">
                All
              </option>
              {this.STATUS_OPTIONS.map(status =>
                <option value={status} key={`statuscol-${status}`}>
                  {status}
                </option>
              )}
            </Select>
          </InputGroup>
          <div styleName="padder" />
          <InputGroup>
            <span>License type</span>
            <Select
              onChange={this.selectLicenseTypeToFilter}
              value={licenseTypeToFilter}
            >
              <option value={this.FILTER_OPTION_ALL} key="ltcol-all">
                All
              </option>
              {this.props.licenseTypes.map(lt =>
                <option value={lt.id} key={`ltcol-${lt.nameId}`}>
                  {lt.nameId}
                </option>
              )}
            </Select>
          </InputGroup>
          <div styleName="padder" />
          <InputGroup>
            <span>Retailer</span>
            <Select
              onChange={this.selectRetailerToFilter}
              value={retailerToFilter}
            >
              <option value={this.FILTER_OPTION_ALL} key="retailercol-all">
                All
              </option>
              {this.props.retailers.map(retailer =>
                <option value={retailer.id} key={`reatilercol-${retailer.id}`}>
                  {retailer.name}
                </option>
              )}
            </Select>
          </InputGroup>
          <div styleName="padder" />
          <InputGroup>
            <span>Retailer person</span>
            <Select
              onChange={this.selectRetailerPersonToFilter}
              value={retailerPersonToFilter}
            >
              <option
                value={this.FILTER_OPTION_ALL}
                key="retailerpersoncol-all"
              >
                All
              </option>
              {this.props.retailerPersons.map(retailerPerson =>
                <option
                  value={retailerPerson.id}
                  key={`ltcol-${retailerPerson.id}`}
                >
                  {retailerPerson.email}
                </option>
              )}
            </Select>
          </InputGroup>
        </div>
        <div styleName='table-controls'>
          <InputGroup>
            <Input
              type='text'
              placeholder={intl.formatMessage(messages.searchInputPlaceholder)}
              onChange={this.search}
              value={searchString}
            />
          </InputGroup>
          <div styleName='control-separator'>|</div>
          <InputGroup horizontal>
            <Select onChange={this.selectDateToFilter} value={dateToFilter}>
              {this.dateCols.map(c =>
                <option value={c} key={`datecol-${c}`}>
                  {this.props.intl.formatMessage(propMessages[c])}
                </option>
              )}
            </Select>
            <div styleName='datepicker-wrap'>
              <DatePicker
                customInput={<Input />}
                onChange={this.getDateRangeSelector('dateFrom')}
                placeholderText={
                  intl.formatMessage(messages.dateFromInputPlaceholder)
                }
                selected={dateFrom}
              />
              {dateFrom && (
                <span
                  styleName='close'
                  onClick={() => this.getDateRangeSelector('dateFrom')(null)}
                >
                  <MdClear />
                </span>
              )}
            </div>
            <div styleName='datepicker-wrap'>
              <DatePicker
                customInput={<Input />}
                onChange={this.getDateRangeSelector('dateTo')}
                placeholderText={
                  intl.formatMessage(messages.dateToInputPlaceholder)
                }
                selected={dateTo}
              />
              {dateTo && (
                <span
                  styleName='close'
                  onClick={() => this.getDateRangeSelector('dateTo')(null)}
                >
                  <MdClear />
                </span>
              )}
            </div>
          </InputGroup>
          <div styleName='control-separator'>|</div>
          {this.props.view === this.BILLING_VIEW && (
            <React.Fragment>
              <button onClick={this.showUnbilled}>
                Show unbilled
              </button>
              <div styleName='control-separator'>|</div>
              <button onClick={this.showUnpaid}>
                Show unpaid
              </button>
              <div styleName='control-separator'>|</div>
            </React.Fragment>
          )}
          <Button onClick={this.updateRows}>
            Päivitä
          </Button>
          <Button onClick={this.exportExcel}>
            {this.props.view === this.LICENSE_VIEW
              ? <FormattedMessage {...messages.exportLicensesButtonLabel} />
              : <FormattedMessage {...messages.exportBillingButtonLabel} />
            }
          </Button>
        </div>
        <div styleName='table-container'>
          <table styleName='table'>
            <thead>
              <Row header>
                {cols.map(col =>
                  <Cell
                    header
                    key={`header-${col}`}
                    onClick={this.getSortSwitcher(col)}
                    sortCol={col === orderBy}
                  >
                    <FormattedMessage {...propMessages[col]} />
                    <Sort
                      asc={orderBy === col && order === this.ORDER_ASC}
                      desc={orderBy === col && order === this.ORDER_DESC}
                    />
                  </Cell>
                )}
              </Row>
            </thead>
            <tbody>
              {this.orderedRows.map(row =>
                <Row
                  key={row.id}
                  active={activeRows.indexOf(row.id) !== -1}
                  clickHandler={this.getRowClickHandler(row.id)}
                >
                  {cols.map(col =>
                    <Cell key={col} sortCol={col === orderBy}>
                      {/*
                        This control structure is bit blatant here, but don't
                        know where else to put it right now.
                      */}
                      {col === 'company.name'
                        ? (
                          <Link
                            to={{
                              pathname: getCompanyDashboardPath(
                                row.company.countryCode,
                                row.company.businessId
                              ),
                              search: `?lang=${intl.locale}`,
                            }}
                            target='_blank'
                            data-click-handler='ignore'
                          >
                            {row.get(col)}
                          </Link>
                        ) : (
                          row.get(col)
                        )}
                    </Cell>
                  )}
                </Row>
              )}
            </tbody>
          </table>
        </div>
        {this.renderPagination()}
        {loading && <LoadingIndicator />}
      </div>
    );
  };
}
LicenseTable.propTypes = propTypes;
LicenseTable.defaultProps = defaultProps;

export default injectIntl(LicenseTable);
